import { ref, watch } from 'vue'
import groupBy from 'lodash/groupBy'
import omit from 'lodash/omit'
import { Dictionary } from 'lodash'
import {
  BBTreeviewNode,
  DateNode,
  CheckboxNode,
  LocationFilters,
  FlatFilter,
  NestedFilter,
  CustomFieldFilter,
  ReportFilters,
  FilterOption
} from '../types'
import { isCheckboxNode } from '../utils/filters/filters.utils'

type TaxonomyFilters = Pick<ReportFilters, 'brand' | 'division' | 'category' | 'season' | 'year'>
const taxonomyFields = ['brand', 'division', 'category', 'season', 'year']

// In retrospect this piece of global state would have been better suited for vuex
const model = ref<ReportFilters>({})

export function useFilters() {
  // Initiate model state from local storage
  try {
    const filters = localStorage.getItem('filter-panel-filters')
    if (filters) {
      model.value = JSON.parse(filters)
    }
  } catch (err) {
    console.warn('Error reading filter-panel-filters from local storage ', err)
  }

  function setSelected(items: Array<BBTreeviewNode>, value: boolean): void {
    items.forEach(item => {
      if (isCheckboxNode(item)) {
        item.selected = value
      }
    })
  }

  function allSelected(items: Array<BBTreeviewNode>): boolean {
    return items.every(item => isCheckboxNode(item) && item.selected)
  }

  function someSelected(items: Array<BBTreeviewNode>): boolean {
    if (allSelected(items)) return false
    return items.some(item => isCheckboxNode(item) && item.selected)
  }

  watch(model, () => {
    localStorage.setItem('filter-panel-filters', JSON.stringify(model.value))
  })

  /**
   * Used by the treeview component, adds entries to the model via the groupingKey
   * (e.g. brand)
   */
  function treeviewDataToFilter(items: Array<CheckboxNode>): void {
    const groupedByFiltername = groupBy(items, 'groupingKey')

    const existingStateWithoutTaxonomies: ReportFilters = omit(model.value, taxonomyFields) // clear all taxonomy fields
    const newTaxonomyModel: TaxonomyFilters = {}
    Object.entries(groupedByFiltername).forEach(([groupingKey, filterValues]) => {
      if (taxonomyFields.includes(groupingKey)) {
        const key = groupingKey as keyof TaxonomyFilters
        const updatedTaxonomyValues: Array<FilterOption> = Array.from(
          new Set(filterValues.map(({ value }) => value))
        ) as Array<FilterOption>
        newTaxonomyModel[key] = updatedTaxonomyValues
      }
    })

    model.value = {
      ...existingStateWithoutTaxonomies,
      ...newTaxonomyModel
    }
  }

  /**
   * low level update api to update a specific key. Used by 'flat' filter types
   * e.g. cancelled
   */
  function set<K extends keyof ReportFilters>(key: K, value: unknown): void {
    model.value = {
      ...model.value,
      [key]: value
    }
  }

  /**
   * low level update api to clear all filters.
   * e.g. onUnmounted
   */
  function clear(): void {
    model.value = {}
  }

  /**
   * Takes UI input and the current state of custom_fields in the model and
   * updates the model with a new value for custom_fields
   * @param inputs input from UI checkbox (i.e. a selection change)
   * @param snapshot snapshot off of the model of custom_fields filters
   */
  function customFieldsToFilter(inputs: Array<BBTreeviewNode>, snapshot: Array<CustomFieldFilter>): void {
    const groupedByFiltername = groupBy(inputs, 'groupingKey')
    const idsInState = snapshot?.map(entry => entry[0])

    const updates = snapshot?.length ? updateExistingCustomFields(groupedByFiltername, snapshot) : []
    const additions = formatAdditionalCustomFields(groupedByFiltername, idsInState)

    const updatedCustomFields = [...updates, ...additions] as Array<CustomFieldFilter>
    model.value = {
      ...model.value,
      custom_fields: updatedCustomFields
    }
  }

  /**
   * Takes UI input from a single date selection and removes the element
   * by id from the current state, before updating the element with the input dates
   * @param input input from UI date field (i.e. a selection change)
   * @param snapshot snapshot off of the model of custom_fields filters
   */
  function upsertCustomFieldDate(input: DateNode, snapshot: Array<CustomFieldFilter>) {
    const idToUpsert = input.groupingKey
    const updatedSnapshot =
      snapshot?.filter((item: any) => {
        const [idInState, _values] = item
        return idInState !== idToUpsert
      }) || []
    const dateDto: { start?: string; end?: string } = {}
    if (typeof input.value === 'object') {
      const start = input.value?.start
      const end = input.value?.end
      if (start) {
        dateDto.start = new Date(start).toISOString()
      } else {
        delete dateDto.start
      }
      if (end) {
        dateDto.end = new Date(end).toISOString()
      } else {
        delete dateDto.end
      }
    }
    const updatedEntry = idToUpsert && (dateDto?.start || dateDto?.end) ? [idToUpsert, dateDto] : []
    const updatedFields = [...updatedSnapshot, updatedEntry] as Array<CustomFieldFilter>
    model.value = {
      ...model.value,
      custom_fields: updatedFields
    }
  }

  /**
   * Takes UI input and a snapshot of the current custom_fields. If the snapshot
   * contains an id from the UI input, update that element, otherwise return the
   * existing element
   * @private
   */
  function updateExistingCustomFields(
    inputDictionary: Dictionary<[BBTreeviewNode, ...BBTreeviewNode[]]>,
    snapshot: Array<CustomFieldFilter>
  ) {
    const updates = snapshot.map(pair => {
      const [idInState, values] = pair as [string, Array<any>]
      const incomingValues = inputDictionary[idInState]
      if (incomingValues) {
        const removeValues = unselectedValues(incomingValues)
        const addValues = selectedValues(incomingValues)
        const keep = values.filter(v => !removeValues.includes(v))
        const updatedValues = [...keep, ...addValues]
        if (!updatedValues.length) return null
        return [idInState, updatedValues]
      } else {
        return [idInState, values]
      }
    })
    return updates.filter(item => item && item[0])
  }

  /**
   * Receives UI input and formats new inputs (skipping existing ids) already in state
   * into the model format for the server DTO
   * @private
   */
  function formatAdditionalCustomFields(
    inputDictionary: Dictionary<[BBTreeviewNode, ...BBTreeviewNode[]]>,
    idsInState: Array<string>
  ) {
    return Object.entries(inputDictionary)
      .filter(([customFieldId, _entries]) => customFieldId && !idsInState?.includes(customFieldId))
      .map(([customFieldId, entries]) => [customFieldId, selectedValues(entries)])
  }

  /**
   * Helper to grab the selected values from Checkbox nodes
   * @private
   */
  function selectedValues(items: Array<BBTreeviewNode>) {
    return items.filter(item => isCheckboxNode(item) && item.selected).map(({ value }) => value)
  }

  /**
   * Helper to grab the unselected values from Checkbox nodes
   * @private
   */
  function unselectedValues(items: Array<BBTreeviewNode>) {
    return items.filter(item => isCheckboxNode(item) && !item.selected).map(({ value }) => value)
  }

  /**
   *
   * @param items LocationFilters[] - loops over and sets any object.selected to false
   */
  function unselectAll(items: LocationFilters[]) {
    items.forEach(filter => {
      const flatFilter = filter as FlatFilter
      const nestedFilter = filter as NestedFilter

      if (flatFilter.options) {
        flatFilter.options.forEach(option => (option.selected = false))
      }

      if (nestedFilter.panels)
        nestedFilter.panels.forEach(panel => {
          if (panel.children)
            panel.children.forEach(child => {
              if (isCheckboxNode(child)) child.selected = false
              child.children?.forEach(c => {
                if (isCheckboxNode(c)) c.selected = false
              })
            })
        })
    })
  }

  return {
    // data
    model,
    // apis
    set,
    clear,
    unselectAll,
    allSelected,
    setSelected,
    someSelected,
    treeviewDataToFilter,
    customFieldsToFilter,
    upsertCustomFieldDate
  }
}
